import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { get, postFile } from "../../api";
import { getPresets } from "../../api/presets";
import Pagination from "../../components/Pagination";
import FileUploadModal from "./components/FileUploadModal";
import CompressionTable from "./components/CompressionTable";
import { useRetryJob } from "../../hooks/useRetryJob";
import {
  pulseAnimation,
  defaultCompressionOptions,
  // MAX_FILE_SIZE_MB // REMOVED - Keep this constant available if needed elsewhere
} from "../../utils/helpers";
import BatchUploadProgressModal from './components/BatchUploadProgressModal';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { trackCompressionStarted, trackCompressionSuccess, trackCompressionFailed } from '../../utils/analytics';


// Initial state for batch uploads
const initialBatchState = {
  files: [], // { file: File, status: 'pending' | 'uploading' | 'success' | 'error', progress: 0, error: null, jobId: null }
  overallProgress: 0, // Percentage of files completed/in progress
  currentFileIndex: -1,
  status: 'idle', // 'idle' | 'uploading' | 'complete' | 'error'
  options: defaultCompressionOptions // Store options used for the batch
};

// --- Main Component ---
const RecentCompressions = forwardRef(function RecentCompressions({ onUpload, embedded = false, searchTerm }, ref) {
  const [currentPage, setCurrentPage] = useState(1);
  const [compressions, setCompressions] = useState([]);
  const [totalCompressions, setTotalCompressions] = useState(0);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [advancedOptions, setAdvancedOptions] = useState(defaultCompressionOptions);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [draggingTable, setDraggingTable] = useState(false);
  const [draggingModal, setDraggingModal] = useState(false);
  const [fileError, setFileError] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pageSize, setPageSize] = useState(5);
  const [credits, setCredits] = useState(null);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presetsError, setPresetsError] = useState('');

  // State for batch uploads
  const [batchUploadState, setBatchUploadState] = useState(initialBatchState);
  const [showBatchProgressModal, setShowBatchProgressModal] = useState(false);

  const tableRef = useRef(null);
  const isUploadingBatch = batchUploadState.status === 'uploading';
  const totalPages = totalCompressions > 0 ? Math.ceil(totalCompressions / pageSize) : 1;

  const handleRetrySuccess = useCallback((updatedJob) => {
    setCompressions((prevCompressions) =>
      prevCompressions.map((c) =>
        c.id === updatedJob.id ? { ...updatedJob, status: updatedJob.status } : c // Ensure status is updated
      )
    );
  }, [setCompressions]);

  const { retryingJobs, retryError, triggerRetry } = useRetryJob(handleRetrySuccess);

  const refreshCredits = useCallback(async () => {
    try {
      const data = await get('/api/1/user/usage');
      setCredits(data.compress_credits);
    } catch (err) {
      console.error('Error refreshing credits:', err);
      // Keep existing credit count or set to 0 on error?
      setCredits(prev => prev !== null ? prev : 0);
    }
  }, []);

  const initCompressions = useCallback(async () => {
    setIsLoading(true);
    const params = new URLSearchParams({
      skip: (currentPage - 1) * pageSize,
      limit: pageSize,
    });
    if (debouncedSearchTerm) {
      params.append('search', debouncedSearchTerm);
    }
    try {
      const data = await get(`/api/1/jobs/job?${params.toString()}`);
      setCompressions(data.jobs || []); // Handle cases where jobs might be null/undefined
      setTotalCompressions(data.total || 0);
    } catch (error) {
      console.error("Failed to fetch compressions:", error);
      setCompressions([]); // Reset on error
      setTotalCompressions(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearchTerm, pageSize]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset page on new search
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    initCompressions();
  }, [currentPage, debouncedSearchTerm, pageSize, initCompressions]); // Include initCompressions dependency

  useEffect(() => {
    refreshCredits(); // Initial credit fetch
  }, [refreshCredits]);

  useEffect(() => {
    const loadPresets = async () => {
      try {
        setPresetsLoading(true);
        setPresetsError('');
        const response = await getPresets();
        setPresets(response.presets || []);

        const defaultPreset = response.presets?.find(p => p.is_default);
        if (defaultPreset) {
          setSelectedPreset(defaultPreset);
          setAdvancedOptions(defaultPreset.options || defaultCompressionOptions);
        } else {
          // Ensure defaults are set if no default preset found
          setAdvancedOptions(defaultCompressionOptions);
          setSelectedPreset(null);
        }
      } catch (err) {
        console.error('Error loading presets:', err);
        setPresetsError(err.message || 'Failed to load presets');
        setPresets([]); // Reset on error
      } finally {
        setPresetsLoading(false);
      }
    };

    loadPresets();
  }, []);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // --- Batch Upload Logic ---
  const runBatchUpload = useCallback(async (batchFiles, batchOptions) => {
    const totalFiles = batchFiles.length;
    if (totalFiles === 0) {
      setBatchUploadState(initialBatchState); // Reset if no files somehow
      return;
    }

    for (let i = 0; i < totalFiles; i++) {
      const currentFile = batchFiles[i].file;
      const currentFileIndex = i;

      // Update state for the current file starting upload
      setBatchUploadState(prev => ({
        ...prev,
        status: 'uploading',
        currentFileIndex: currentFileIndex,
        files: prev.files.map((f, index) =>
          index === currentFileIndex ? { ...f, status: 'uploading', progress: 0 } : f
        ),
        overallProgress: (i / totalFiles) * 100 // Progress based on starting files
      }));

      try {
        const response = (await postFile("/api/1/files/upload/sync", currentFile, {
          progressCallback: (progress) => {
            setBatchUploadState(prev => ({
              ...prev,
              files: prev.files.map((f, index) =>
                index === currentFileIndex ? { ...f, progress: progress } : f
              )
            }));
          },
          headers: {
            "x-compression-options": JSON.stringify(batchOptions),
          },
        }))[0];

        // Track successful compression
        if (response.id && response.original_file_size && response.compressed_file_size) {
          trackCompressionSuccess(
            response.id,
            response.original_file_size,
            response.compressed_file_size,
            response.compression_ratio
          );
        }

        // Update state for the successfully uploaded file
        setBatchUploadState(prev => ({
          ...prev,
          files: prev.files.map((f, index) =>
            index === currentFileIndex ? { ...f, status: 'success', jobId: response.id, progress: 100, compressed_file_url: response.compressed_url } : f
          )
        }));

        // Notify parent (if provided) and broadcast a global event for dashboards/widgets
        try { onUpload && onUpload(); } catch (_) { }
        try { window.dispatchEvent(new CustomEvent('jobs:updated', { detail: { jobId: response.id } })); } catch (_) { }

      } catch (err) {
        console.error(`Upload failed for ${currentFile.name}:`, err);

        let errorMessage = 'Upload failed. Please try again.';

        // Try to extract specific error message
        if (err.data && err.data.error) {
          errorMessage = err.data.error;
        } else if (err.message) {
          errorMessage = err.message;
        }

        // Track failed compression
        trackCompressionFailed(currentFile.name, errorMessage);

        // Update state for the failed file and stop the batch
        setBatchUploadState(prev => ({
          ...prev,
          status: 'error',
          files: prev.files.map((f, index) =>
            index === currentFileIndex ? { ...f, status: 'error', error: errorMessage, progress: 0 } : f
          ),
          overallProgress: ((i + 1) / totalFiles) * 100 // Update progress to reflect failed item
        }));

        await initCompressions(); // Refresh list after error
        await refreshCredits();   // Refresh credits after error
        return; // Stop processing the rest of the batch
      }
    }

    // If loop completes without errors
    setBatchUploadState(prev => ({
      ...prev,
      status: 'complete',
      currentFileIndex: -1,
      overallProgress: 100
    }));

    await initCompressions(); // Refresh list after successful batch
    await refreshCredits();   // Refresh credits after successful batch

  }, [onUpload, initCompressions, refreshCredits]);

  const handleStartBatchUpload = useCallback((filesToUpload, options) => {
    if (filesToUpload.length === 0) return;

    // Check credits again right before starting (important!)
    const needed = filesToUpload.length;
    if (credits !== null && credits < needed) {
      setFileError(`Not enough credits. You need ${needed} but have ${credits}. Please purchase more.`);
      setShowFileUploadModal(true); // Re-open modal to show error
      return;
    }

    // Track compression start
    const totalSize = filesToUpload.reduce((sum, file) => sum + file.size, 0);
    trackCompressionStarted(filesToUpload.length, totalSize, options);

    const initialFilesState = filesToUpload.map(file => ({
      file: file,
      status: 'pending',
      progress: 0,
      error: null,
      jobId: null
    }));

    setBatchUploadState({
      files: initialFilesState,
      overallProgress: 0,
      currentFileIndex: -1,
      status: 'uploading', // Set initial status to uploading to show modal
      options: options
    });

    setShowBatchProgressModal(true); // Show the progress modal

    // Start the actual upload process (async, don't wait here)
    runBatchUpload(initialFilesState, options);

  }, [credits, runBatchUpload, setFileError]);

  // Retry the entire batch from the beginning
  const handleRetryBatch = useCallback(() => {
    if (!batchUploadState.files.length) return;
    // Reset status and errors before restarting
    const filesToRetry = batchUploadState.files.map(f => ({ ...f, status: 'pending', progress: 0, error: null, jobId: null }));

    handleStartBatchUpload(filesToRetry.map(f => f.file), batchUploadState.options);

  }, [batchUploadState.files, batchUploadState.options, handleStartBatchUpload]);

  // --- Drag and Drop for Table (Simplified for initiating upload) ---
  // Global drag and drop handling
  useEffect(() => {
    const handleGlobalDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (isUploadingBatch || (credits !== null && credits < 1)) return;
      if (!e.dataTransfer.types.includes('Files')) return;

      e.dataTransfer.dropEffect = "copy";
      setDraggingTable(true);
    };

    const handleGlobalDragEnter = (e) => {
      if (isUploadingBatch || (credits !== null && credits < 1)) return;
      if (!e.dataTransfer.types.includes('Files')) return;

      setDraggingTable(true);
    };

    const handleGlobalDragLeave = (e) => {
      // If the drag leaves to outside the window or to an element that
      // is a direct child of the document body, consider it a leave
      if (!e.relatedTarget || e.relatedTarget === document.body) {
        setDraggingTable(false);
      }
    };

    const handleGlobalDragEnd = () => {
      setDraggingTable(false);
    };

    const handleGlobalDrop = () => {
      setDraggingTable(false);
    };

    const handleKeyDown = (e) => {
      // ESC key cancels drag operation
      if (e.key === 'Escape' && draggingTable) {
        setDraggingTable(false);
      }
    };

    // Only add listeners if not in upload modal
    if (!showFileUploadModal) {
      // Add global event listeners
      document.addEventListener('dragover', handleGlobalDragOver);
      document.addEventListener('dragenter', handleGlobalDragEnter);
      document.addEventListener('dragleave', handleGlobalDragLeave);
      document.addEventListener('dragend', handleGlobalDragEnd);
      document.addEventListener('drop', handleGlobalDrop);
      document.addEventListener('keydown', handleKeyDown);
    }

    // Cleanup on unmount
    return () => {
      document.removeEventListener('dragover', handleGlobalDragOver);
      document.removeEventListener('dragenter', handleGlobalDragEnter);
      document.removeEventListener('dragleave', handleGlobalDragLeave);
      document.removeEventListener('dragend', handleGlobalDragEnd);
      document.removeEventListener('drop', handleGlobalDrop);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isUploadingBatch, credits, draggingTable, showFileUploadModal]);

  // Simplified drop handler for the actual drop functionality
  const handleDropTable = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent browser download behavior
    setDraggingTable(false);
    if (isUploadingBatch || (credits !== null && credits < 1)) return;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      // Don't process files here, just open the modal
      setFileError(""); // Clear any previous general errors
      setShowFileUploadModal(true);

      // Store the dropped files in a ref to be processed by the modal when it mounts
      droppedFilesRef.current = droppedFiles;
    }
  };

  // Add a ref to store dropped files
  const droppedFilesRef = useRef(null);

  const handleNestedOptionChange = (group, optionKey, value) => {
    setAdvancedOptions((prev) => ({
      ...prev,
      [group]: { ...prev[group], [optionKey]: value },
    }));
  };

  const closeFileUploadModal = () => {
    setShowFileUploadModal(false);
    setFileError(""); // Clear general errors when closing manually
    droppedFilesRef.current = null; // Clear any stored dropped files
    // Resetting options/advanced toggle can be done inside FileUploadModal's close handler if needed
    // setShowAdvanced(false);
  };

  useImperativeHandle(ref, () => ({
    openUpload: () => {
      if (!isUploadingBatch && (credits === null || credits > 0)) {
        setShowFileUploadModal(true);
      }
    }
  }), [isUploadingBatch, credits]);

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    if (!isNaN(newSize) && newSize > 0) {
      setPageSize(newSize);
      setCurrentPage(1); // Reset to page 1 on size change
    }
  };

  const handlePresetSelect = useCallback((preset) => {
    if (preset) {
      setSelectedPreset(preset);
      setAdvancedOptions(preset.options || defaultCompressionOptions);
    } else {
      setSelectedPreset(null);
      setAdvancedOptions(defaultCompressionOptions); // Reset to default if preset deselected
    }
  }, []);

  // Effect to warn user if they try to leave the page during upload
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Standard way to show browser confirmation dialog
      event.preventDefault();
      event.returnValue = ''; // Required for Chrome
      return ''; // Required for older browsers
    };

    if (isUploadingBatch) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isUploadingBatch]); // Re-run this effect when isUploadingBatch changes

  return (
    <>
      <style>{pulseAnimation}</style>
      <div className={`${embedded ? 'min-h-[380px]' : 'bg-background-elevated rounded-lg shadow-lg p-6 mt-6 min-h-[380px] border border-border'}`}>
        <div
          ref={tableRef}
          onDrop={handleDropTable}
          className={`rounded-lg transition-colors duration-150 relative ${draggingTable ? 'outline outline-2 outline-offset-0 outline-accent' : ''
            }`}
        >
          {/* Drag overlay */}
          {draggingTable && (
            <div className="absolute inset-0 bg-accent/70 rounded-lg z-10 flex flex-col items-center justify-center">
              <div className="text-foreground text-xl font-semibold mb-2">Drop your files here</div>
              <div className="text-accent text-sm">Release to upload</div>

              {/* Upload icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-accent/80 mt-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3-3m0 0l3 3m-3-3v12" />
              </svg>
            </div>
          )}

          {/* Minimal status bar (no controls). Errors and upload state show here. */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {!embedded && (
                <h2 className="text-xl font-semibold text-foreground">Recent compressions</h2>
              )}
            </div>
            <div className="flex items-center gap-3">
              {retryError && (
                <div className="text-danger text-sm p-2 bg-danger-soft border border-danger-border rounded">
                  {retryError}
                </div>
              )}
              {isUploadingBatch && (
                <button
                  onClick={() => setShowBatchProgressModal(true)}
                  className="text-accent hover:text-accent/90 flex items-center gap-1 text-sm"
                  title="Show upload progress"
                >
                  <InformationCircleIcon className="h-5 w-5 animate-spin" style={{ animationDuration: '2s' }} />
                  <span>Uploading...</span>
                </button>
              )}
            </div>
          </div>

          <CompressionTable
            compressions={compressions}
            onUploadClick={() => setShowFileUploadModal(true)}
            draggingTable={draggingTable}
            isLoading={isLoading && !isUploadingBatch}
            hasSearchTerm={!!debouncedSearchTerm}
            onRetryClick={triggerRetry}
            retryingJobs={retryingJobs}
            currentBatchFiles={batchUploadState.files.filter(f => f.status === 'uploading' || f.status === 'pending').map(f => f.file.name)}
          />

          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center text-sm text-muted-foreground">
              <span>Show:</span>
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                className="ml-2 bg-background-soft border border-border rounded-md px-2 py-1 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
              <span className="ml-2">entries per page</span>
            </div>

            {totalPages > 1 && (
              <Pagination currentPage={currentPage} totalPages={totalPages} paginate={paginate} />
            )}
          </div>
        </div>
      </div>

      {showFileUploadModal && (
        <FileUploadModal
          fileError={fileError}
          setFileError={setFileError}
          showAdvanced={showAdvanced}
          setShowAdvanced={setShowAdvanced}
          advancedOptions={advancedOptions}
          setAdvancedOptions={setAdvancedOptions}
          onNestedChange={handleNestedOptionChange}
          dragging={draggingModal}
          setDragging={setDraggingModal}
          closeModal={closeFileUploadModal}
          credits={credits}
          selectedPreset={selectedPreset}
          presets={presets}
          onPresetSelect={handlePresetSelect}
          presetsLoading={presetsLoading}
          presetsError={presetsError}
          onStartBatchUpload={handleStartBatchUpload}
          initialFiles={droppedFilesRef.current}
        />
      )}

      {showBatchProgressModal && batchUploadState.status !== 'idle' && (
        <BatchUploadProgressModal
          batchState={batchUploadState}
          onClose={() => {
            setShowBatchProgressModal(false);
            const tableRow = document.querySelector('table tr');
            if (tableRow) tableRow.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          onRetryBatch={handleRetryBatch}
        />
      )}
    </>
  );
});

export default RecentCompressions;
